import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '../../components/feature/SiteLayout';
import MovieCard from '../../components/base/MovieCard';
import FilterBar, { type CategoryFilterState } from './components/FilterBar';
import { movies } from '../../mocks/movies';
import { filterMovies, resolveCategoryMeta, sortMovies, type SortKey } from '../../utils/movies';

const PAGE_SIZE = 8;

const SORT_OPTIONS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'updated', label: '按更新时间', icon: 'ri-time-line' },
  { key: 'views', label: '按热度', icon: 'ri-fire-line' },
  { key: 'year', label: '按上映时间', icon: 'ri-calendar-line' },
];

const defaultFilters: CategoryFilterState = { region: '全部', year: null, genre: '全部', minRating: 0 };

export default function Category() {
  const { type = 'movie' } = useParams();
  const meta = useMemo(() => resolveCategoryMeta(type), [type]);

  const [filters, setFilters] = useState<CategoryFilterState>(defaultFilters);
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<'pagination' | 'loadmore'>('pagination');

  const decodedType = decodeURIComponent(type);
  const isFixedCategory = decodedType === 'movie' || decodedType === 'tv' || decodedType === 'featured';

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
    setSortKey('updated');
  }, [type]);

  useEffect(() => {
    document.title = `${meta.title} - 中文字幕永久在线`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `${meta.subtitle}。中文字幕永久在线提供高清中文字幕${meta.title}在线观看。`);
  }, [meta]);

  const filtered = useMemo(() => {
    const base = meta.baseList(movies);
    const genreFilter = isFixedCategory ? filters.genre : '全部';
    const result = filterMovies(base, {
      region: filters.region,
      year: filters.year,
      genre: genreFilter,
      minRating: filters.minRating,
    });
    return sortMovies(result, sortKey);
  }, [meta, filters, sortKey, isFixedCategory]);

  const visibleCount = mode === 'loadmore' ? page * PAGE_SIZE : PAGE_SIZE;
  const visible =
    mode === 'loadmore'
      ? filtered.slice(0, visibleCount)
      : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const hasMore = page * PAGE_SIZE < filtered.length;

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8 bg-background-50">
        <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
          <span className="text-foreground-800">{meta.title}</span>
        </nav>

        <header className="mb-6 md:mb-8">
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-2">{meta.title}</h1>
          <p className="text-sm md:text-base text-foreground-600">{meta.subtitle}</p>
          <p className="text-xs text-foreground-400 mt-2">共找到 {filtered.length} 部相关影视作品</p>
        </header>

        <FilterBar
          value={filters}
          onChange={(next) => {
            setFilters(next);
            setPage(1);
          }}
          hideGenreFilter={isFixedCategory && decodedType !== 'featured'}
          onReset={resetFilters}
        />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-6 mb-5">
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-sort-desc text-lg text-primary-500" aria-hidden="true"></i>
            排序方式
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  setSortKey(option.key);
                  setPage(1);
                }}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sortKey === option.key
                    ? 'bg-accent-500 text-secondary-950 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                <i className={option.icon} aria-hidden="true"></i>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {visible.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {visible.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="flex flex-col items-center gap-5 mt-10">
              {mode === 'pagination' ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPage(index + 1)}
                      className={`w-9 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        page === index + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
                  </button>
                </div>
              ) : hasMore ? (
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  className="flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 hover:border-primary-400 hover:text-primary-600 text-foreground-700 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-add-line" aria-hidden="true"></i>
                  加载更多
                </button>
              ) : (
                <p className="text-sm text-foreground-400">已经到底啦，没有更多内容了</p>
              )}

              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                {[
                  { key: 'pagination', label: '分页翻页' },
                  { key: 'loadmore', label: '加载更多' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setMode(item.key as 'pagination' | 'loadmore');
                      setPage(1);
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      mode === item.key ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-inbox-2-line text-3xl" aria-hidden="true"></i>
            </span>
            <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-2">没有找到符合条件的影视</h3>
            <p className="text-sm text-foreground-500 mb-6">试试更换筛选条件，或者浏览其他分类的内容</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选
              </button>
              <Link
                to="/category/movie"
                className="flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 hover:border-primary-400 hover:text-primary-600 text-foreground-700 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                浏览高清电影
              </Link>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}